import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { Color } from "Components/Form/Button";
import { Banner } from "Components/Generic/Banner";
import { useAppState } from "Context/StateContext";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { CompanyProfile, ShipmentType } from "Util/enums";

import { Button, Input } from "../../../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../../../components/Form/AutoSuggest";
import { Commodity } from "../../../../../../../../../models/commodity";
import { Option } from "../../../../../../../../../models/option";
import { mapBannerTypeByTrackingShipmentStatus } from "../../../../../utils/Waybills.util";
import { useShipmentTrackingHook } from "../../../../hooks/shipmentTrackingHook";
import { useShipmentContext } from "../../../../hooks/useContext";
import { DefaultProps } from "../../../../types";
import { SectionTitle } from "../../SectionTitle";
import { ViewModeComponent } from "./view-mode";

interface AirFormServiceProps extends DefaultProps {}

export function AirFormService(props: AirFormServiceProps) {
  const { onViewMode } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const { shipment, setShipment, commodities } = useShipmentContext();
  const isShipmentTrackingFetched = useMemo(() => shipment?.fetchedData, [shipment]);

  const isShowFetchDetails = useMemo(() => {
    return (
      (currentUser?.company?.airTrackingEnabled && shipment?.shipmentType === ShipmentType.AIR) ||
      (currentUser?.company?.seaTrackingEnabled && shipment?.shipmentType === ShipmentType.SEA)
    );
  }, [currentUser]);
  const [{ getShipmentTrackingById }] = useShipmentTrackingHook();

  const {
    data: shipmentTracking,
    status: shipmentTrackingStatus,
    isFetching: isShipmentTrackingFetching,
    refetch: getShipmentTrackingByIdRefetch,
  } = getShipmentTrackingById(
    shipment?.airwayBill ?? "",
    {
      enabled: false,
      select: (response) => {
        return response?.data || {};
      },
    },
    "Air"
  );

  const hasMarineTraffic = useMemo(() => !!shipment?.marineTrafficId, [shipment]);
  const [showTrackingStatusBanner, setShowTrackingStatusBanner] = useState(false);
  const [bannerInfo, setBannerInfo] = useState<any>({});
  const onGetCommodityInputValue = () => {
    if (shipment?.commodity) {
      const commoditiesValue = shipment.commodity.split("/");
      const commoditiesArr = commodities?.filter((option) =>
        commoditiesValue.includes(option.value)
      );

      return commoditiesArr;
    }

    return [];
  };

  const renderTrackingStatusBanner = useCallback(() => {
    return (
      showTrackingStatusBanner && (
        <Grid item xs={12}>
          <Banner
            type={bannerInfo?.type}
            onClose={() => {
              setBannerInfo({});
              setShowTrackingStatusBanner(false);
            }}
            label={bannerInfo?.label}
            prefix={bannerInfo?.prefix}
          />
        </Grid>
      )
    );
  }, [showTrackingStatusBanner, bannerInfo]);

  useEffect(() => {
    if (!isShipmentTrackingFetching) {
      setBannerInfo(
        mapBannerTypeByTrackingShipmentStatus(shipmentTrackingStatus, shipmentTracking)
      );
    }
    if (!_.isEmpty(shipmentTracking) && shipmentTrackingStatus === "success") {
      const updatedShipment = {
        ...shipment,
        fetchedData: true,
        flightNumber: shipmentTracking.vesselName || shipment?.vesselName,
        airline: shipmentTracking.carrier || shipment?.voyageNumber,
        shipmentTracking: {
          ...shipment?.shipmentTracking,
          finalPortPredictiveArrivalLast:
            shipmentTracking?.finalPortPredictiveArrivalUtcLast ||
            shipment?.shipmentTracking?.finalPortPredictiveArrivalLast,
          originPortPredictiveDeparture:
            shipmentTracking?.originPortPredictiveDepartureUtc ||
            shipment?.shipmentTracking?.originPortPredictiveDeparture,
          finalPortPredictiveArrival:
            shipmentTracking?.finalPortPredictiveArrivalUtc ||
            shipment?.shipmentTracking?.finalPortPredictiveArrival,
          originPortActualDeparture:
            shipmentTracking.originPortActualDepartureUtc ||
            shipment?.shipmentTracking?.originPortActualDeparture,
          finalPortActualArrival:
            shipmentTracking.finalPortActualArrivalUtc ||
            shipment?.shipmentTracking?.finalPortActualArrival,
          originPort: shipmentTracking.originPortName,
          finalPort: shipmentTracking.finalPortName,
        },
      };

      setShipment(updatedShipment);
    }
  }, [shipmentTracking, shipmentTrackingStatus, isShipmentTrackingFetching]);

  useEffect(() => {
    if (!_.isEmpty(shipment?.id)) {
      getShipmentTrackingByIdRefetch();
    }
  }, []);

  useEffect(() => {
    setShowTrackingStatusBanner(false);
  }, [shipment?.airwayBill]);

  const disabledToFetchDetails = useMemo(() => {
    return (
      !!shipment?.id &&
      (isShipmentTrackingFetching || isShipmentTrackingFetched || hasMarineTraffic)
    );
  }, [shipment, isShipmentTrackingFetching, isShipmentTrackingFetched, hasMarineTraffic]);

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <SectionTitle title={translate("service")} />
      </Grid>
      <Grid container item direction="row" spacing={3} rowGap={3}>
        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: `${translate("houseAirWaybill")} #`,
              value: shipment?.airwayBill,
            }}
          >
            <Input
              label={`${translate("houseAirWaybill")} #`}
              name="airwayBill"
              required
              value={shipment?.airwayBill}
              onChange={(event) => setShipment({ ...shipment, airwayBill: event.target.value })}
            />
          </ViewModeComponent>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          lg={3}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "end",
          }}
        >
          {!onViewMode && isShowFetchDetails && (
            <Button
              onClick={() => {
                if (shipment?.airwayBill) {
                  setBannerInfo(mapBannerTypeByTrackingShipmentStatus("loading", {}));
                  setShowTrackingStatusBanner(true);
                  getShipmentTrackingByIdRefetch();
                }
              }}
              disabled={disabledToFetchDetails}
              loading={isShipmentTrackingFetching}
              customColor={Color.SECONDARY_DARK}
              style={{ width: "100%" }}
            >
              {translate("fetchDetails")}
            </Button>
          )}
        </Grid>
      </Grid>
      {renderTrackingStatusBanner()}
      <Grid container item direction="row" spacing={3} rowGap={3}>
        <Grid item xs={12} md={6} lg={6}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: translate("hsCommodityCode"),
              value: shipment?.commodity,
            }}
          >
            <AutoSuggest
              label={translate("hsCommodityCode")}
              placeholder={translate("selectAnOption")}
              options={commodities}
              required={currentUser?.company?.profile?.initials === CompanyProfile.MV}
              getOptionLabel={(option: Commodity) => option.label}
              getOptionSelected={(option: Commodity, value: Commodity) =>
                option.value === value.value
              }
              value={onGetCommodityInputValue()}
              multiple
              onChange={(options: Option[]) => {
                if (options?.length) {
                  const optionsString = options.map((opt) => opt.value).join("/");

                  setShipment({ ...shipment, commodity: optionsString });
                } else {
                  setShipment({ ...shipment, commodity: undefined });
                }
              }}
            />
          </ViewModeComponent>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: `${translate("flight")} #`,
              value: shipment?.flightNumber,
            }}
          >
            <Input
              label={`${translate("flight")} #`}
              name="flightNumber"
              disabled={isShipmentTrackingFetched || hasMarineTraffic}
              value={shipment?.flightNumber}
              onChange={(event) => setShipment({ ...shipment, flightNumber: event.target.value })}
            />
          </ViewModeComponent>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <ViewModeComponent
            onViewMode={onViewMode}
            formData={{
              label: translate("airline"),
              value: shipment?.airline,
            }}
          >
            <Input
              label={translate("airline")}
              name="airline"
              disabled={isShipmentTrackingFetched || hasMarineTraffic}
              value={shipment?.airline}
              onChange={(event) => setShipment({ ...shipment, airline: event.target.value })}
            />
          </ViewModeComponent>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
